import React, { useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import TimeGutter from './TimeGutter';
import EmployeeColumnHeader from './EmployeeColumnHeader';
import AppointmentLabelCard from './AppointmentLabelCard';
import {
  getDayTimeGrid,
  getAppointmentBounds,
  layoutOverlappingBlocks,
  getGridBodyHeightPx,
  getGridSlotsHeightPx,
  SLOT_HEIGHT_PX,
  GRID_CLOSE_FOOTER_PX,
} from '../utils/timeGrid';
import { calendarItemBelongsToColumn } from '../utils/columnMatch';
import { openCalendarItem } from '../utils/openCalendarItem';
import { useHorizontalColumnVirtual } from '../hooks/useHorizontalColumnVirtual';
import { useTeamCalMobileLayout } from '../hooks/useTeamCalMobileLayout';
import { useCalendarDragDrop } from '../drag-drop/CalendarDragDropContext';
import '../styles/TeamLabelCalendar.css';

export default function TeamLabelCalendarGrid({
  selectedDate,
  companyHours,
  columns,
  appointmentsForDay,
  stickyHeading = null,
}) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const isMobileLayout = useTeamCalMobileLayout();
  const stickySidebar = !isMobileLayout;
  const { dropPreview, dropAvailability } = useCalendarDragDrop() || {};
  const dayKey = format(selectedDate, 'yyyy-MM-dd');

  const grid = useMemo(
    () => getDayTimeGrid(selectedDate, companyHours),
    [selectedDate, companyHours]
  );

  const {
    start,
    end,
    leadingSpacerWidth,
    trailingSpacerWidth,
    innerWidth,
    onScroll,
    fillViewport,
    gutterWidthPx,
    columnWidthPx,
  } = useHorizontalColumnVirtual({
    scrollRef,
    columnCount: columns.length,
    stickySidebar,
  });

  const visibleColumns = useMemo(() => {
    if (end < start || columns.length === 0) return [];
    return columns.slice(start, end + 1);
  }, [columns, start, end]);

  const appointmentsByColumnId = useMemo(() => {
    const map = new Map(columns.map((col) => [col.id, []]));
    appointmentsForDay.forEach((item) => {
      for (const col of columns) {
        if (calendarItemBelongsToColumn(item, col.id)) {
          map.get(col.id).push(item);
          break;
        }
      }
    });
    return map;
  }, [appointmentsForDay, columns]);

  const placedByColumnId = useMemo(() => {
    if (!grid) return new Map();
    const map = new Map();
    columns.forEach((col) => {
      const colAppts = appointmentsByColumnId.get(col.id) || [];
      const withBounds = colAppts
        .map((a) => {
          const b = getAppointmentBounds(a);
          if (!b) return null;
          return { appointment: a, start: b.start, end: b.end };
        })
        .filter(Boolean);
      map.set(col.id, layoutOverlappingBlocks(withBounds, grid.gridStart, grid.gridEnd));
    });
    return map;
  }, [columns, appointmentsByColumnId, grid]);

  const handleOpen = (item) => openCalendarItem(navigate, item);

  if (!grid) {
    return (
      <div className="team-cal-v3__closed">
        <p>Closed or no business hours for {format(selectedDate, 'EEEE, MMM d')}.</p>
        <p className="team-cal-v3__closed-hint">
          Set company hours in Settings if you expect this day to be open.
        </p>
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <p className="team-cal-v3-muted team-cal-v3-shell__empty">No columns to show. Adjust the Employee filter.</p>
    );
  }

  const gridBodyHeight = getGridBodyHeightPx(grid);
  const gridSlotsHeight = getGridSlotsHeightPx(grid);
  const totalMinWidth = gutterWidthPx + columns.length * columnWidthPx;
  const colShellClass = fillViewport ? 'team-cal-v3__col--fluid' : 'team-cal-v3__col--fixed';
  const headShellClass = fillViewport ? 'team-cal-v3__col-head--fluid' : 'team-cal-v3__col-head--fixed';

  return (
    <div
      className={[
        'team-cal-v3__scroll',
        'team-cal-v3__scroll--stacked',
        fillViewport ? 'team-cal-v3__scroll--fit' : '',
        isMobileLayout ? 'team-cal-v3__scroll--mobile' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      ref={scrollRef}
      onScroll={onScroll}
      style={
        fillViewport
          ? undefined
          : { '--team-cal-col-w': `${columnWidthPx}px` }
      }
    >
      <div
        className="team-cal-v3__grid-wrap"
        style={{
          minWidth: fillViewport ? '100%' : totalMinWidth,
          width: fillViewport ? '100%' : totalMinWidth,
        }}
      >
        <div className="team-cal-v3__sticky-top">
          {stickyHeading ? <div className="team-cal-v3__sticky-top-heading">{stickyHeading}</div> : null}
          <div className="team-cal-v3__header-row">
            <div
              className={`team-cal-v3__corner${stickySidebar ? ' team-cal-v3__corner--sticky' : ''}`}
              style={{ width: gutterWidthPx }}
            />
            <div className="team-cal-v3__virtual-cols" style={{ width: innerWidth, minWidth: innerWidth }}>
              {leadingSpacerWidth > 0 ? (
                <div
                  className="team-cal-v3__virtual-spacer"
                  style={{ width: leadingSpacerWidth, flexShrink: 0 }}
                  aria-hidden
                />
              ) : null}
            {visibleColumns.map((col, colIdx) => (
              <EmployeeColumnHeader
                key={col.id}
                className={`${headShellClass}${
                  colIdx === visibleColumns.length - 1 ? ' team-cal-v3__col-head--last-visible' : ''
                }`.trim()}
                style={
                  fillViewport
                    ? undefined
                    : {
                        width: columnWidthPx,
                        flex: `0 0 ${columnWidthPx}px`,
                        boxSizing: 'border-box',
                      }
                }
                name={col.name}
                count={(appointmentsByColumnId.get(col.id) || []).length}
              />
            ))}
              <div
                className="team-cal-v3__virtual-spacer"
                style={{ width: trailingSpacerWidth, flexShrink: 0 }}
                aria-hidden
              />
            </div>
          </div>
        </div>

        <div
          className="team-cal-v3__body"
          data-team-cal-grid-body="true"
          data-team-cal-day={dayKey}
          data-team-cal-grid-start-ms={grid.gridStart.getTime()}
          data-team-cal-grid-end-ms={grid.gridEnd.getTime()}
        >
          <div
            style={{ width: gutterWidthPx, flexShrink: 0 }}
            className={`team-cal-v3__gutter-wrap${stickySidebar ? ' team-cal-v3__gutter-wrap--sticky' : ''}`}
          >
            <TimeGutter
              slots={grid.slots}
              gridEnd={grid.gridEnd}
              closeFooterPx={GRID_CLOSE_FOOTER_PX}
              className="team-cal-v3__gutter--sticky"
            />
          </div>

          <div
            className="team-cal-v3__virtual-cols team-cal-v3__virtual-cols--body"
            style={{ width: innerWidth, minWidth: innerWidth, position: 'relative' }}
          >
            {leadingSpacerWidth > 0 ? (
              <div
                className="team-cal-v3__virtual-spacer"
                style={{ width: leadingSpacerWidth, flexShrink: 0 }}
                aria-hidden
              />
            ) : null}
            {visibleColumns.map((col, idx) => {
              const placed = placedByColumnId.get(col.id) || [];

              return (
                <div
                  key={col.id}
                  className={`team-cal-v3__col ${colShellClass}${
                    idx === visibleColumns.length - 1 ? ' team-cal-v3__col--last-visible' : ''
                  }${
                    dropPreview?.dayKey === dayKey &&
                    dropPreview?.targetEmployeeId === col.id
                      ? dropAvailability?.isAvailable === false
                        ? ' team-cal-v3__col--drop-invalid'
                        : dropAvailability?.isAvailable === true
                          ? ' team-cal-v3__col--drop-valid'
                          : ' team-cal-v3__col--drop-pending'
                      : ''
                  }`.trim()}
                  data-team-cal-column-id={col.id}
                  data-team-cal-column-name={col.name}
                  style={{
                    height: gridBodyHeight,
                    minHeight: gridBodyHeight,
                    ...(fillViewport
                      ? {}
                      : { width: columnWidthPx, flex: `0 0 ${columnWidthPx}px` }),
                  }}
                >
                  {grid.slots.map((slot, slotIdx) => (
                    <div
                      key={slot.time?.toISOString?.() || slotIdx}
                      className="team-cal-v3__slot"
                      style={{ height: SLOT_HEIGHT_PX }}
                    />
                  ))}
                  <div
                    className="team-cal-v3__close-footer"
                    style={{ height: GRID_CLOSE_FOOTER_PX }}
                    aria-hidden
                  />

                  <div
                    className="team-cal-v3__labels-layer"
                    style={{ height: gridSlotsHeight }}
                  >
                    {dropPreview?.dayKey === dayKey &&
                    dropPreview?.targetEmployeeId === col.id ? (
                      <div
                        className={`team-cal-v3__drop-preview${
                          dropAvailability?.isAvailable === false
                            ? ' team-cal-v3__drop-preview--invalid'
                            : dropAvailability?.isAvailable === true
                              ? ' team-cal-v3__drop-preview--valid'
                              : ' team-cal-v3__drop-preview--pending'
                        }`}
                        style={{
                          top: dropPreview.topPx,
                          height: dropPreview.heightPx,
                        }}
                        aria-hidden
                      />
                    ) : null}
                    {placed.map((p) => (
                      <AppointmentLabelCard
                        key={`${p.appointment.type || 'apt'}-${p.appointment.id}`}
                        appointment={p.appointment}
                        style={p.style}
                        sourceColumnId={col.id}
                        onClick={handleOpen}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            <div
              className="team-cal-v3__virtual-spacer"
              style={{ width: trailingSpacerWidth, flexShrink: 0 }}
              aria-hidden
            />
          </div>
        </div>
      </div>
    </div>
  );
}
